// Shared AI-call + upload-URL logic for every builder (calendar, canvas,
// collage, custom-mug, greeting-card, photobook, tshirt, the
// photo-ordering kiosk). AI-call logic was previously copy-pasted
// near-identically into each of those 8 files - already drifted once (see
// docs/Completed-projects.md#16) - extracted so it only ever needs fixing
// in one place. See docs/decisions-to-make.md #17.
//
// Loaded site-wide from theme.liquid (same pattern as theme.js/custom.js),
// after the Firebase compat SDK scripts it depends on.
//
// Requests go through shopify-ai-gateway (Cloud Run), not directly to
// process-ai-image / generate-upload-url-v2, carrying a Firebase App Check
// token so the gateway can verify the request genuinely came from this
// storefront before forwarding with real IAM auth - see
// docs/decisions-to-make.md #3 for the full history.
//
// Every value below (Firebase config, reCAPTCHA Enterprise site key) is
// meant to be public - this is how Firebase web apps are designed to work,
// App Check's real protection is server-side token verification, not
// hiding these. Nothing here is a secret.
window.PhotoStoreAI = (function () {
  var AI_GATEWAY_ENDPOINT = 'https://shopify-ai-gateway-pub5svf4pa-ts.a.run.app';

  var firebaseConfig = {
    projectId: 'massive-folder-459310-t0',
    appId: '1:452888819324:web:38bf9a6f38b3bf4b7a3bf9',
    storageBucket: 'massive-folder-459310-t0.firebasestorage.app',
    apiKey: 'AIzaSyDMRFl-_ib7Pj7zTh5QXMHprIBI_MOW4V4',
    authDomain: 'massive-folder-459310-t0.firebaseapp.com',
    messagingSenderId: '452888819324',
    measurementId: 'G-JRG17BDLL4'
  };
  var RECAPTCHA_ENTERPRISE_SITE_KEY = '6LdraswtAAAAAGTkn7TpPlnbmDBtGRXWNqMalgur';

  var appCheckInstance = null;
  function getAppCheckInstance() {
    if (!appCheckInstance) {
      var app = firebase.initializeApp(firebaseConfig);
      appCheckInstance = firebase.appCheck(app);
      appCheckInstance.activate(
        new firebase.appCheck.ReCaptchaEnterpriseProvider(RECAPTCHA_ENTERPRISE_SITE_KEY),
        true // isTokenAutoRefreshEnabled
      );
    }
    return appCheckInstance;
  }

  // Fire-and-forget - never lets a logging failure affect the real error
  // path, never awaited by callers. See gateway's /log-client-issue for
  // why this exists: with reCAPTCHA Enterprise, a low-scored session
  // (common for VPNs/privacy browsers, per docs/decisions-to-make.md #3)
  // fails to even MINT a token, entirely client-side - the gateway never
  // sees a request for that case at all, so without this there would be
  // zero server-side visibility into real customers being turned away.
  function reportClientIssue(reason, context) {
    try {
      fetch(AI_GATEWAY_ENDPOINT + '/log-client-issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: String(reason).slice(0, 500), context: String(context).slice(0, 200) }),
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* never let telemetry break the caller */ }
  }

  async function getAppCheckToken(context) {
    try {
      var result = await getAppCheckInstance().getToken();
      return result.token;
    } catch (err) {
      reportClientIssue(err && err.message ? err.message : String(err), context);
      throw err;
    }
  }

  async function processImage(payload, maxRetries) {
    if (maxRetries === undefined) maxRetries = 2;
    for (var i = 0; i <= maxRetries; i++) {
      try {
        var token = await getAppCheckToken('processImage:' + (payload && payload.type));
        var res = await fetch(AI_GATEWAY_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Firebase-AppCheck': token
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          if (res.status >= 500 && i < maxRetries) {
            console.log("Cloud Run cold start timeout, retrying AI process in 3s...");
            await new Promise(function (r) { setTimeout(r, 3000); });
            continue;
          }
          throw new Error("AI Failed with status: " + res.status);
        }
        return await res.blob();
      } catch (err) {
        if (i === maxRetries) throw err;
        await new Promise(function (r) { setTimeout(r, 3000); });
      }
    }
  }

  async function getUploadUrl(payload) {
    var token = await getAppCheckToken('getUploadUrl');
    var res = await fetch(AI_GATEWAY_ENDPOINT + '/upload-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': token
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Failed to get upload URL (status ' + res.status + ')');
    return await res.json();
  }

  // Kiosk photo hand-off: the phone that uploads a photo and the kiosk
  // screen that needs to display it are two different browsers, so there's
  // no shared JS memory between them like within one builder page. The
  // phone calls reportKioskPhoto once per processed photo; the kiosk polls
  // getKioskSessionPhotos for everything reported so far under that
  // session. Both are thin gateway routes backed by the Firestore instance
  // shopify-ai-gateway already has for rate-limiting - see
  // functions/shopify-ai-gateway/index.js and docs/decisions-to-make.md
  // (kiosk build).
  async function reportKioskPhoto(payload) {
    var token = await getAppCheckToken('reportKioskPhoto');
    var res = await fetch(AI_GATEWAY_ENDPOINT + '/kiosk-report-photo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': token
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Failed to report kiosk photo (status ' + res.status + ')');
    return await res.json();
  }

  async function getKioskSessionPhotos(payload) {
    var token = await getAppCheckToken('getKioskSessionPhotos');
    var res = await fetch(AI_GATEWAY_ENDPOINT + '/kiosk-session-photos', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': token
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Failed to fetch kiosk session photos (status ' + res.status + ')');
    return await res.json();
  }

  // "Edit a completed project" (decisions-to-make.md #45/#56 Phase 2): the
  // order-actions Customer Account UI Extension sends a buyer here with
  // ?resume_gcs_path=<the order's own _Layout_GCS_Path> - each builder
  // calls this once on load to get that recipe back with every embedded
  // photo's signed accessTokenUrl freshly re-signed (the ones baked into an
  // old recipe are 7 days expired by the time anyone comes back to edit).
  // See functions/shopify-ai-gateway/index.js's handleProjectRecipe.
  async function getProjectRecipe(payload) {
    var token = await getAppCheckToken('getProjectRecipe');
    var res = await fetch(AI_GATEWAY_ENDPOINT + '/project-recipe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': token
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) throw new Error('Failed to fetch project recipe (status ' + res.status + ')');
    return await res.json();
  }

  // Phase 3 of decisions-to-make.md #45/#56 ("save/resume a draft across
  // devices"). A real OAuth 2.0 + PKCE round trip against Shopify's
  // Customer Account API (NOT the old customerAccessTokenCreate mutation,
  // removed in Storefront API 2025-04) - see the plan's Phase 3 section for
  // the full design and why. The session token travels
  // start→callback→exchange→localStorage, never a cookie: the gateway's own
  // domain is third-party relative to this storefront, and third-party
  // cookies are already blocked by default in several browsers.
  var PS_SESSION_STORAGE_KEY = 'ps_session';

  function getPsSession() {
    try {
      var raw = localStorage.getItem(PS_SESSION_STORAGE_KEY);
      if (!raw) return null;
      var session = JSON.parse(raw);
      if (!session || !session.sessionToken || !session.expiresAt) return null;
      if (new Date(session.expiresAt).getTime() <= Date.now()) return null;
      return session;
    } catch (e) {
      return null;
    }
  }

  function clearPsSession() {
    try { localStorage.removeItem(PS_SESSION_STORAGE_KEY); } catch (e) { /* ignore */ }
  }

  // Navigates away (full page load, not a fetch) to Shopify's own login -
  // callers should save any in-progress builder state (e.g. the existing
  // localStorage draft each builder already keeps) before calling this,
  // since the page unloads. returnToUrl should normally be
  // window.location.href so the customer lands right back where they were,
  // minus any ps_exchange param (stripped by exchangePsSessionFromUrl below
  // after a previous round trip, so it never compounds).
  function startCustomerLogin(returnToUrl) {
    window.location.href = AI_GATEWAY_ENDPOINT + '/customer-auth/start?return_to=' + encodeURIComponent(returnToUrl);
  }

  // Runs once per page load (see the self-init call near the bottom of this
  // file, same pattern as initKioskChrome) - if the gateway's
  // /customer-auth/callback just redirected back here with ?ps_exchange=,
  // trade it for the real sessionToken and store that instead. The
  // one-time code never becomes the long-lived credential itself, so a
  // leaked URL (browser history, referrer) only ever exposes a code that's
  // already single-use and ~2 minutes old by the time anyone could act on it.
  async function exchangePsSessionFromUrl() {
    var params = new URLSearchParams(window.location.search);
    var exchangeCode = params.get('ps_exchange');
    if (!exchangeCode) return;

    params.delete('ps_exchange');
    var newSearch = params.toString();
    var newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    window.history.replaceState(null, '', newUrl);

    try {
      var token = await getAppCheckToken('exchangePsSession');
      var res = await fetch(AI_GATEWAY_ENDPOINT + '/customer-auth/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Firebase-AppCheck': token },
        body: JSON.stringify({ exchangeCode: exchangeCode })
      });
      if (!res.ok) throw new Error('Exchange failed with status ' + res.status);
      var data = await res.json();
      localStorage.setItem(PS_SESSION_STORAGE_KEY, JSON.stringify({ sessionToken: data.sessionToken, expiresAt: data.expiresAt }));
    } catch (err) {
      console.error('Failed to complete login:', err);
    }
  }

  // Shared by save/list/delete below - callers get a plain thrown Error
  // with a recognizable 'session_expired' message on an expired/missing
  // session (mirroring the gateway's own {error:'session_expired'} body) so
  // a builder's "Save for later" button can catch it specifically and send
  // the customer through startCustomerLogin rather than show a generic
  // failure - see the plan's "known real gap" note on why this bounce is
  // usually (not always) invisible.
  async function psSessionFetch(path, payload) {
    var session = getPsSession();
    if (!session) throw new Error('session_expired');
    var token = await getAppCheckToken('psSessionFetch:' + path);
    var res = await fetch(AI_GATEWAY_ENDPOINT + path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Firebase-AppCheck': token,
        'X-PS-Session': session.sessionToken
      },
      body: JSON.stringify(payload || {})
    });
    if (res.status === 401) { clearPsSession(); throw new Error('session_expired'); }
    if (!res.ok) throw new Error(path + ' failed with status ' + res.status);
    return await res.json();
  }

  function saveProject(payload) { return psSessionFetch('/projects/save', payload); }
  function listProjects() { return psSessionFetch('/projects/list'); }
  function deleteProject(projectId) { return psSessionFetch('/projects/delete', { projectId: projectId }); }

  // Kiosk bottom chrome: the product-switcher bar + cart badge shown on
  // every builder page in kiosk mode (never online - it only ever inits
  // when ?kiosk_session= is present). One shared copy rather than
  // duplicating this bar's HTML/CSS/JS into all 8 builder files - see
  // decision #17's whole reason this file exists. Each builder calls
  // kioskNotifyAddedToCart() right after its own successful /cart/add.js
  // call instead of navigating to /cart, so the customer can keep
  // building more products in one visit.
  var KIOSK_PRODUCTS = [
    { name: 'Standard Prints', tagline: 'Classic prints, ready in a flash', url: '/pages/photo-ordering-kiosk',
      icon: '<path d="M7 8h11v13H7z"/><path d="M4 4h11v4"/>' },
    { name: 'Photo Canvas', tagline: 'Gallery-wrapped, wall-ready', url: '/products/photo-canvas',
      icon: '<rect x="3" y="3" width="18" height="18" rx="1"/><rect x="6" y="6" width="12" height="12" rx="0.5"/>' },
    { name: 'Photo Blocks', tagline: 'Build a beautiful collage', url: '/products/photo-blocks',
      icon: '<rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="8"/><rect x="3" y="13" width="8" height="8"/><rect x="13" y="13" width="8" height="8"/>' },
    { name: 'Photo Mug', tagline: 'Your photo, every morning', url: '/products/11ozphotomugwithbo',
      icon: '<path d="M3 4h13v9a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V4z"/><path d="M16 7h2a3 3 0 0 1 0 6h-2"/>' },
    { name: 'Photo T-Shirt', tagline: 'Wear your favourite shot', url: '/products/printedshirtwhitel',
      icon: '<path d="M8 3l4 2 4-2 4 4-3 3v11H7V10L4 7z"/>' },
    { name: 'Photo Calendar', tagline: '12 months, all yours', url: '/products/calendara412monthsringbound',
      icon: '<rect x="3" y="5" width="18" height="16" rx="1"/><path d="M3 9h18M8 3v4M16 3v4"/>' },
    { name: 'Greeting Cards', tagline: 'A personal touch, printed', url: '/products/greeting-cards',
      icon: '<path d="M3 5h18v14H3z"/><path d="M3 5l9 7 9-7"/>' },
    { name: 'Photobook', tagline: 'Your story, bound beautifully', url: '/products/photobook-ai',
      icon: '<path d="M4 4h8v16H6a2 2 0 0 1-2-2V4z"/><path d="M20 4h-8v16h6a2 2 0 0 0 2-2V4z"/>' }
  ];

  var kioskCartCountEl = null;
  var kioskToastEl = null;
  var kioskChromeInitialized = false;

  function kioskGetSessionId() {
    return new URLSearchParams(window.location.search).get('kiosk_session');
  }

  async function kioskRefreshCartBadge() {
    if (!kioskCartCountEl) return;
    try {
      var res = await fetch('/cart.js');
      var cart = await res.json();
      kioskCartCountEl.textContent = cart.item_count;
    } catch (e) { /* badge staying stale is harmless */ }
  }

  function kioskShowToast(message) {
    if (!kioskToastEl) return;
    kioskToastEl.textContent = message;
    kioskToastEl.classList.add('ks-chrome-toast-visible');
    clearTimeout(kioskToastEl._hideTimer);
    kioskToastEl._hideTimer = setTimeout(function () {
      kioskToastEl.classList.remove('ks-chrome-toast-visible');
    }, 2500);
  }

  function kioskNotifyAddedToCart() {
    kioskRefreshCartBadge();
    kioskShowToast('✓ Added to cart');
  }

  function initKioskChrome() {
    var kioskSessionId = kioskGetSessionId();
    if (!kioskSessionId || kioskChromeInitialized) return;
    kioskChromeInitialized = true;

    var style = document.createElement('style');
    style.textContent =
      '#ks-chrome-bar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 9999; background: #1a1512; ' +
      'border-top: 1px solid #3a342c; display: flex; align-items: center; gap: 6px; padding: 8px 10px; overflow-x: auto; }' +
      '.ks-chrome-tile { flex: 0 0 auto; display: flex; flex-direction: column; align-items: center; gap: 3px; ' +
      'background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; ' +
      'padding: 6px 10px; cursor: pointer; color: #fff; min-width: 64px; text-align: center; }' +
      '.ks-chrome-tile:hover { background: rgba(255,255,255,0.14); }' +
      '.ks-chrome-tile svg { width: 18px; height: 18px; color: #fab617; }' +
      '.ks-chrome-tile span { font-size: 0.65rem; font-weight: 700; line-height: 1.1; }' +
      '#ks-chrome-cart { margin-left: auto; flex: 0 0 auto; display: flex; align-items: center; gap: 8px; ' +
      'background: #fab617; color: #111; border-radius: 999px; padding: 10px 18px; font-weight: 800; cursor: pointer; ' +
      'white-space: nowrap; }' +
      '#ks-chrome-toast { position: fixed; left: 50%; bottom: 76px; transform: translate(-50%, 10px); z-index: 10000; ' +
      'background: #10b981; color: #fff; font-weight: 800; padding: 10px 22px; border-radius: 999px; ' +
      'box-shadow: 0 8px 24px rgba(0,0,0,0.3); opacity: 0; pointer-events: none; transition: opacity 0.2s ease, transform 0.2s ease; }' +
      '.ks-chrome-toast-visible { opacity: 1 !important; transform: translate(-50%, 0) !important; }' +
      'body { padding-bottom: 64px; }';
    document.head.appendChild(style);

    var bar = document.createElement('div');
    bar.id = 'ks-chrome-bar';
    bar.innerHTML = KIOSK_PRODUCTS.map(function (p) {
      return '<div class="ks-chrome-tile" data-url="' + p.url + '">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' + p.icon + '</svg>' +
        '<span>' + p.name + '</span></div>';
    }).join('') + '<div id="ks-chrome-cart">Cart &middot; <span id="ks-chrome-cart-count">0</span></div>';
    document.body.appendChild(bar);

    Array.prototype.forEach.call(bar.querySelectorAll('.ks-chrome-tile'), function (tile) {
      tile.addEventListener('click', function () {
        var baseUrl = tile.getAttribute('data-url');
        var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
        window.location.href = baseUrl + sep + 'kiosk_session=' + encodeURIComponent(kioskSessionId);
      });
    });
    document.getElementById('ks-chrome-cart').addEventListener('click', function () {
      window.location.href = '/cart';
    });

    var toast = document.createElement('div');
    toast.id = 'ks-chrome-toast';
    document.body.appendChild(toast);

    kioskCartCountEl = document.getElementById('ks-chrome-cart-count');
    kioskToastEl = toast;
    kioskRefreshCartBadge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKioskChrome);
  } else {
    initKioskChrome();
  }

  // Fire-and-forget, same as initKioskChrome above - every builder page
  // picks up a pending ?ps_exchange= for free, no per-builder wiring needed.
  exchangePsSessionFromUrl();

  return {
    processImage: processImage,
    getUploadUrl: getUploadUrl,
    reportKioskPhoto: reportKioskPhoto,
    getKioskSessionPhotos: getKioskSessionPhotos,
    getProjectRecipe: getProjectRecipe,
    kioskNotifyAddedToCart: kioskNotifyAddedToCart,
    KIOSK_PRODUCTS: KIOSK_PRODUCTS,
    getPsSession: getPsSession,
    startCustomerLogin: startCustomerLogin,
    saveProject: saveProject,
    listProjects: listProjects,
    deleteProject: deleteProject
  };
})();
